import { RefObject, useEffect, useState } from "react";

function isElementInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  return rect.bottom > 0 && rect.top < viewportHeight;
}

/** True once `revealAfterRef` has scrolled offscreen (lede no longer intersecting). */
export function useRevealNavVisibility(
  revealAfterRef: RefObject<HTMLElement | null>,
): boolean {
  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    let observer: IntersectionObserver | null = null;

    const attach = (): void => {
      const target = revealAfterRef.current;
      if (!target) {
        return;
      }

      observer?.disconnect();

      setShowNav(!isElementInViewport(target));

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) {
            return;
          }
          setShowNav(!entry.isIntersecting);
        },
        { root: null, threshold: 0 },
      );
      observer.observe(target);
    };

    attach();
    const rafId = requestAnimationFrame(attach);

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [revealAfterRef]);

  return showNav;
}
